import type { z } from "zod";
import type {
    GlBasis,
    glIssuePeriodSchema,
    MigrationFileType,
} from "@common/schemas/accounting-integration.schema";
import type { ImportRejection } from "@common/schemas/domains/accounting/import-rejection.schema";

export type MigrationPeriod = z.infer<typeof glIssuePeriodSchema>;

export type MigrationRowStatus =
    | "awaiting"
    | "extracting"
    | "ready"
    | "has_warnings"
    | "basis_required"
    | "has_issues"
    | "unrecognized";

export type MigrationDocStatus = "extracting" | "extracted" | "unrecognized";

export const ROLE_LABELS: Record<MigrationFileType, string> = {
    coa: "CoA",
    cashGl: "Cash GL",
    accrualGl: "Accrual GL",
    unknownGl: "General ledger",
};

/**
 * Status text shown in an upload row: a terse label inline in the grid cell
 * (≤18 chars) and a slightly longer explanation on hover (≤35 chars).
 */
export interface MigrationStatusText {
    short: string;
    tooltip: string;
}

export const OVERLAP_MESSAGE: MigrationStatusText = {
    short: "Duplicate period",
    tooltip: "Overlaps an uploaded period",
};
export const OUTSIDE_PERIOD_MESSAGE: MigrationStatusText = {
    short: "Mismatched period",
    tooltip: "Outside the required period",
};
export const NO_TRANSACTIONS_MESSAGE: MigrationStatusText = {
    short: "No transactions",
    tooltip: "No dated transactions found",
};
export const DUPLICATE_COA_MESSAGE: MigrationStatusText = {
    short: "Duplicate",
    tooltip: "Chart of accounts already added",
};
export const ACCRUAL_NOT_NEEDED_MESSAGE: MigrationStatusText = {
    short: "Not needed",
    tooltip: "No accrual ledger for this entity",
};
export const ACCRUAL_LEDGER_MISSING_MESSAGE: MigrationStatusText = {
    short: "No accrual books",
    tooltip: "Select cash basis instead",
};
export const BASIS_REQUIRED_MESSAGE: MigrationStatusText = {
    short: "Basis required",
    tooltip: "Choose a cash or accrual basis",
};
export const STARTS_BEFORE_MIGRATION_MESSAGE: MigrationStatusText = {
    short: "Starts early",
    tooltip: "Starts before the migration start date",
};
export const STARTS_AFTER_MIGRATION_MESSAGE: MigrationStatusText = {
    short: "Starts late",
    tooltip: "Starts after the migration start date",
};

/** A single uploaded file, with whatever the extraction has read back so far. */
export interface MigrationDoc {
    documentId: number;
    fileName: string;
    status: MigrationDocStatus;
    role?: MigrationFileType;
    period?: MigrationPeriod | null;
    // Basis the user picked for a label-less (`unknownGl`) export.
    chosenBasis?: GlBasis;
    summary?: string;
    statusMessage?: MigrationStatusText;
    rejection?: ImportRejection;
    warnings?: string[];
    // Parser warnings the user has acknowledged one at a time via "Proceed anyway".
    dismissedParserWarningCount?: number;
}

export interface MigrationMatchedRow {
    key: string;
    role: MigrationFileType;
    period: MigrationPeriod | null;
    status: MigrationRowStatus;
    fileName?: string;
    documentId?: number;
    summary?: string;
    statusMessage?: MigrationStatusText;
    rejection?: ImportRejection;
    // A label-less GL keeps its basis selector even once matched, so the user
    // can re-pick after choosing.
    isUnknownBasis?: boolean;
}

export interface MigrationUnverifiedDoc {
    documentId: number;
    fileName: string;
    status: "extracting" | "unrecognized" | "basis_required" | "has_warnings";
    period: MigrationPeriod | null;
    message?: MigrationStatusText;
    isUnknownBasis?: boolean;
    rejection?: ImportRejection;
}

/**
 * A non-blocking finalize warning the user confirms before completing
 * (irreversible — one migration per entity). `startDate` flags GL coverage that
 * begins before/after the migration start; `missingBasis` flags an expected
 * basis the user omitted entirely (it won't be imported). Coverage gaps are
 * blockers, not warnings, so they never appear here.
 */
export type FinalizeWarningKind = "startDate" | "missingBasis";

export interface FinalizeWarning {
    kind: FinalizeWarningKind;
    message: string;
}

export interface MigrationMatchResult {
    rows: MigrationMatchedRow[];
    unverifiedDocs: MigrationUnverifiedDoc[];
    isCoaCovered: boolean;
    // Populated only when no expected basis is provided at all (hard blocker).
    // When at least one basis is provided, omitted bases become finalize
    // warnings instead and this is empty.
    uncoveredBasisLabels: string[];
    isBasisSelectionRequired: boolean;
    finalizeWarnings: FinalizeWarning[];
    // Earliest coverage start across ready GL files; null until one is ready.
    earliestGlStart: string | null;
}
